import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  Field,
  InlineAlert,
  Spinner,
} from '@aeris/ui'

import { importProductCategories } from '../lib/api/product-categories.ts'
import { ApiError } from '../lib/api.ts'
import {
  parseCategorySheet,
  type CategorySheet,
} from '../lib/category-sheet.ts'

/**
 * As categorias por planilha — F243.
 *
 * Nasceu para copiar a árvore do site de Ipatinga para a loja de GV: um CSV
 * `codigo;categoria;subcategoria`. Como no "Classificar pelo nome" (F163), o
 * botão de gravar só aparece depois da prévia, e o servidor só toca em produto
 * SEM categoria — tela, bateria e o que alguém marcou à mão ficam como estão.
 */

export type ImportCategoriesDialogProps = {
  onClose: () => void
  /** Chamado depois de gravar, para a lista se recarregar. */
  onDone: () => void
}

const plural = (count: number, one: string, many: string): string =>
  `${count.toLocaleString('pt-BR')} ${count === 1 ? one : many}`

/**
 * O corpo da importação, sem a janela: a planilha, a prévia e o "Gravar".
 * Vai no diálogo de Produtos e em Importar → "Subir só as categorias".
 */
export const ImportCategoriesPanel = ({
  canWrite = true,
  onClose,
  onDone,
}: {
  canWrite?: boolean
  /** Sem ele, não há "Cancelar": a tela de Importar não fecha. */
  onClose?: (() => void) | undefined
  onDone?: (() => void) | undefined
}): ReactNode => {
  const queryClient = useQueryClient()
  const [sheet, setSheet] = useState<CategorySheet | null>(null)
  const [error, setError] = useState<string | null>(null)

  const failure = (fallback: string) => (cause: unknown) => {
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const preview = useMutation({
    mutationFn: async (rows: CategorySheet['rows']) =>
      await importProductCategories({ rows: [...rows], dryRun: true }),
    onError: failure('Não foi possível montar a prévia.'),
  })

  const apply = useMutation({
    mutationFn: async (rows: CategorySheet['rows']) =>
      await importProductCategories({ rows: [...rows], dryRun: false }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['product-categories'] })
      onDone?.()
    },
    onError: failure('Não foi possível gravar as categorias.'),
  })

  const read = async (file: File): Promise<void> => {
    setError(null)
    preview.reset()
    apply.reset()
    const parsed = parseCategorySheet(await file.text())
    setSheet(parsed)
    if (parsed.rows.length === 0) {
      setError('A planilha não tem nenhuma linha com código e categoria.')
      return
    }
    preview.mutate(parsed.rows)
  }

  const outcome = apply.data ?? preview.data

  return (
    <div className="flex flex-col gap-3">
      <Field label="Planilha (.csv)">
        {(field) => (
          <input
            id={field.id}
            type="file"
            accept=".csv,.txt,text/csv"
            disabled={preview.isPending || apply.isPending}
            className="text-(length:--text-sm)"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file !== undefined) void read(file)
            }}
          />
        )}
      </Field>

      {sheet !== null && sheet.skippedLines.length > 0 ? (
        <InlineAlert tone="warning">
          {plural(sheet.skippedLines.length, 'linha', 'linhas')} sem código ou
          sem categoria {sheet.skippedLines.length === 1 ? 'ficou' : 'ficaram'}{' '}
          de fora (linha {sheet.skippedLines.slice(0, 10).join(', ')}
          {sheet.skippedLines.length > 10 ? '…' : ''}).
        </InlineAlert>
      ) : null}

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {preview.isPending ? <Spinner /> : null}

      {outcome === undefined ? null : (
        <div
          className="flex flex-col gap-1 text-(length:--text-sm) text-(--text-secondary)"
          aria-live="polite"
        >
          {outcome.applied ? (
            <InlineAlert tone="success">Categorias gravadas.</InlineAlert>
          ) : null}
          <p>
            <strong>{plural(outcome.marked, 'produto', 'produtos')}</strong>{' '}
            {outcome.applied ? 'ganharam' : 'vão ganhar'} categoria.
          </p>
          <p>
            {plural(
              outcome.alreadyCategorized,
              'produto já tinha',
              'produtos já tinham'
            )}{' '}
            categoria e {outcome.applied ? 'ficaram' : 'ficam'} como
            {outcome.alreadyCategorized === 1 ? ' estava' : ' estavam'}.
          </p>
          {outcome.categoriesCreated.length === 0 ? null : (
            <p>
              Categorias novas ({outcome.categoriesCreated.length}):{' '}
              {outcome.categoriesCreated.join(', ')}.
            </p>
          )}
          {outcome.subcategoriesCreated.length === 0 ? null : (
            <p>
              {plural(
                outcome.subcategoriesCreated.length,
                'subcategoria nova',
                'subcategorias novas'
              )}
              .
            </p>
          )}
          {outcome.unknownCount === 0 ? null : (
            <p className="text-(--text-muted)">
              {plural(
                outcome.unknownCount,
                'código não existe',
                'códigos não existem'
              )}{' '}
              aqui: {outcome.unknownCodes.join(', ')}
              {outcome.unknownCount > outcome.unknownCodes.length ? '…' : ''}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {onClose === undefined ? null : (
          <Button variant="ghost" onClick={onClose}>
            {apply.data === undefined ? 'Cancelar' : 'Fechar'}
          </Button>
        )}
        {apply.data === undefined ? (
          <Button
            variant="primary"
            disabled={
              !canWrite ||
              sheet === null ||
              preview.data === undefined ||
              preview.data.marked === 0 ||
              apply.isPending
            }
            onClick={() => {
              if (sheet !== null) apply.mutate(sheet.rows)
            }}
          >
            {apply.isPending ? <Spinner /> : 'Gravar as categorias'}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export const ImportCategoriesDialog = ({
  onClose,
  onDone,
}: ImportCategoriesDialogProps): ReactNode => (
  <Dialog
    open
    onOpenChange={(next) => {
      if (!next) onClose()
    }}
  >
    <DialogContent
      title="Importar planilha de categorias"
      description="Um CSV com código; categoria; subcategoria. Cria a categoria e a subcategoria que faltam e marca só os produtos que ainda não têm categoria."
      className="max-w-2xl"
    >
      <ImportCategoriesPanel onClose={onClose} onDone={onDone} />
    </DialogContent>
  </Dialog>
)
